import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTeam } from '@/contexts/TeamContext';
import {
  ChatCircle, PaperPlaneRight, SpinnerGap, Trash, PencilSimple,
  ArrowBendDownRight, X, User
} from '@phosphor-icons/react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const API = `${window.location.origin}/api`;

function CommentBubble({ comment, currentUserId, teamId, onReply, onDelete, onEdit, depth = 0 }) {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const isOwn = comment.user_id === currentUserId;

  const handleSave = async () => {
    if (!editContent.trim()) return;
    await onEdit(comment.comment_id, editContent.trim());
    setEditing(false);
  };

  const timeAgo = (() => {
    try { return formatDistanceToNow(parseISO(comment.created_at), { addSuffix: true }); }
    catch { return ''; }
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${depth > 0 ? 'ml-8 border-l-2 border-slate-100 pl-4' : ''}`}
      data-testid={`comment-${comment.comment_id}`}
    >
      <div className="flex items-start gap-2.5 py-2">
        {comment.author_picture ? (
          <img src={comment.author_picture} alt="" className="w-7 h-7 rounded-full border border-slate-200 flex-shrink-0 mt-0.5" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <User size={13} className="text-slate-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-semibold text-slate-800">{comment.author_name || 'Unknown'}</span>
            <span className="text-[11px] text-slate-400">{timeAgo}</span>
            {comment.updated_at !== comment.created_at && (
              <span className="text-[10px] text-slate-400 italic">(edited)</span>
            )}
          </div>
          {editing ? (
            <div className="flex gap-2">
              <input type="text" value={editContent} onChange={(e) => setEditContent(e.target.value)}
                className="flex-1 text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:border-navy focus:ring-2 focus:ring-blue-100"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
                autoFocus data-testid={`comment-edit-input-${comment.comment_id}`} />
              <button onClick={handleSave} className="text-xs font-medium text-navy">Save</button>
              <button onClick={() => setEditing(false)} className="text-xs text-slate-400">Cancel</button>
            </div>
          ) : (
            <p className="text-sm text-slate-700 leading-relaxed">{comment.content}</p>
          )}
          {!editing && (
            <div className="flex items-center gap-3 mt-1">
              <button onClick={() => onReply(comment.comment_id)} className="text-[11px] font-medium text-slate-400 hover:text-navy transition-colors flex items-center gap-1"
                data-testid={`reply-btn-${comment.comment_id}`}>
                <ArrowBendDownRight size={11} /> Reply
              </button>
              {isOwn && (
                <>
                  <button onClick={() => { setEditing(true); setEditContent(comment.content); }}
                    className="text-[11px] font-medium text-slate-400 hover:text-navy transition-colors flex items-center gap-1"
                    data-testid={`edit-btn-${comment.comment_id}`}>
                    <PencilSimple size={11} /> Edit
                  </button>
                  <button onClick={() => onDelete(comment.comment_id)}
                    className="text-[11px] font-medium text-slate-400 hover:text-red-500 transition-colors flex items-center gap-1"
                    data-testid={`delete-btn-${comment.comment_id}`}>
                    <Trash size={11} /> Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function TeamComments({ taskId, teamId, currentUserId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teams/${teamId}/tasks/${taskId}/comments`, { withCredentials: true });
      setComments(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [teamId, taskId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/teams/${teamId}/tasks/${taskId}/comments`,
        { content: content.trim(), parent_id: replyTo },
        { withCredentials: true });
      setContent('');
      setReplyTo(null);
      fetchComments();
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  };

  const handleEdit = async (commentId, newContent) => {
    try {
      await axios.put(`${API}/teams/${teamId}/comments/${commentId}`,
        { content: newContent }, { withCredentials: true });
      fetchComments();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}/comments/${commentId}`, { withCredentials: true });
      fetchComments();
    } catch (err) { console.error(err); }
  };

  // Build thread tree
  const rootComments = comments.filter(c => !c.parent_id);
  const repliesMap = {};
  comments.forEach(c => {
    if (c.parent_id) {
      if (!repliesMap[c.parent_id]) repliesMap[c.parent_id] = [];
      repliesMap[c.parent_id].push(c);
    }
  });

  const replyingToComment = replyTo ? comments.find(c => c.comment_id === replyTo) : null;

  return (
    <div className="border-t border-slate-200 pt-4 mt-4" data-testid="team-comments-section">
      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <ChatCircle size={16} weight="duotone" className="text-navy" />
        Comments
        {comments.length > 0 && <span className="text-xs font-normal text-slate-400">({comments.length})</span>}
      </h3>

      {loading ? (
        <div className="flex justify-center py-4"><SpinnerGap size={20} className="text-navy animate-spin" /></div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-slate-400 mb-3">No comments yet. Start the conversation.</p>
      ) : (
        <div className="space-y-0.5 mb-3 max-h-[300px] overflow-y-auto pr-1">
          {rootComments.map(c => (
            <div key={c.comment_id}>
              <CommentBubble comment={c} currentUserId={currentUserId} teamId={teamId}
                onReply={setReplyTo} onDelete={handleDelete} onEdit={handleEdit} depth={0} />
              {(repliesMap[c.comment_id] || []).map(r => (
                <CommentBubble key={r.comment_id} comment={r} currentUserId={currentUserId} teamId={teamId}
                  onReply={setReplyTo} onDelete={handleDelete} onEdit={handleEdit} depth={1} />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Reply indicator */}
      <AnimatePresence>
        {replyingToComment && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-2 text-xs text-slate-500 mb-1.5 bg-slate-50 rounded-md px-2 py-1">
            <ArrowBendDownRight size={12} />
            <span>Replying to <strong>{replyingToComment.author_name}</strong></span>
            <button onClick={() => setReplyTo(null)} className="ml-auto"><X size={12} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handlePost} className="flex items-center gap-2">
        <input type="text" value={content} onChange={(e) => setContent(e.target.value)}
          placeholder={replyTo ? 'Write a reply...' : 'Add a comment...'}
          className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-navy focus:ring-2 focus:ring-blue-100 bg-white"
          data-testid="comment-input" />
        <button type="submit" disabled={posting || !content.trim()}
          className="bg-navy text-white disabled:opacity-40 p-2 rounded-lg transition-all hover:bg-navy-hover"
          data-testid="post-comment-btn">
          {posting ? <SpinnerGap size={16} className="animate-spin" /> : <PaperPlaneRight size={16} weight="fill" />}
        </button>
      </form>
    </div>
  );
}

export default TeamComments;
